import React, { useState, useEffect } from 'react';
import { X, User, FileText, Calendar, Plus, Loader2 } from 'lucide-react';
import { caseService } from '../../services/caseService';
import AssessmentDetailsModal from './AssessmentDetailsModal';

const CaseDetailsModal = ({ isOpen, onClose, case: caseItem, onNewAssessment, token }) => {
  const [fullCaseData, setFullCaseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);

  // Fetch full case details when modal opens
  useEffect(() => {
    if (isOpen && caseItem) {
      fetchCaseDetails();
    }
    
    // Reset state when modal closes
    if (!isOpen) {
      setFullCaseData(null);
      setError(null);
      setSelectedAssessment(null);
      setShowAssessmentModal(false);
    }
  }, [isOpen, caseItem?.caseId]);

  const fetchCaseDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await caseService.getCase(caseItem.caseId, token);
      setFullCaseData(data);
    } catch (err) {
      console.error('Error fetching case details:', err);
      setError('Failed to load case details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAssessment = (assessment) => {
    setSelectedAssessment(assessment);
    setShowAssessmentModal(true);
  };

  const handleCloseAssessmentModal = () => {
    setShowAssessmentModal(false);
    setSelectedAssessment(null);
  };

  if (!isOpen || !caseItem) return null;

  // Use full case data if available, otherwise use summary data from list
  const displayCase = fullCaseData || caseItem;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
              <User className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{caseItem.name}</h2>
              <p className="text-teal-100 text-sm">ID: {caseItem.caseId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            // Loading state
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="animate-spin text-teal-600 mb-4" size={48} />
              <p className="text-gray-600">Loading case details...</p>
            </div>
          ) : error ? (
            // Error state
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600 font-semibold mb-2">Error</p>
              <p className="text-red-700 text-sm mb-4">{error}</p>
              <button
                onClick={fetchCaseDetails}
                className="text-sm text-red-600 hover:text-red-700 font-semibold hover:underline"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
          {/* Case Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-teal-50 border-2 border-teal-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-teal-700 mb-1">
                <FileText size={20} />
                <span className="font-semibold">Total Assessments</span>
              </div>
                  <div className="text-3xl font-bold text-teal-900">{displayCase.assessmentCount || 0}</div>
            </div>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-700 mb-1">
                <Calendar size={20} />
                <span className="font-semibold">Last Assessment</span>
              </div>
              <div className="text-sm font-semibold text-blue-900">
                    {displayCase.lastAssessmentDate ? formatDate(displayCase.lastAssessmentDate) : 'No assessments yet'}
              </div>
            </div>
          </div>

          {/* Assessment History */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Assessment History</h3>
            
                {displayCase.assessments && displayCase.assessments.length > 0 ? (
              <div className="space-y-3">
                    {displayCase.assessments.map((assessment, index) => (
                  <div
                    key={assessment.assessmentId || index}
                    className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 hover:border-teal-300 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText size={16} className="text-teal-600" />
                          <span className="font-semibold text-gray-900">
                                Assessment #{displayCase.assessments.length - index}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar size={14} />
                              <span>{formatDate(assessment.createdAt || assessment.date)}</span>
                            </div>
                            {assessment.summary && (
                              <div className="mt-2 flex gap-4 text-xs text-gray-600">
                                <div>
                                  <span className="font-semibold">FIM/FAM Total: </span>
                                  <span className="text-teal-700 font-bold">{assessment.summary.totalScore}</span>
                                  <span className="text-gray-500"> / {assessment.summary.domainCount * 7}</span>
                                </div>
                                <div>
                                  <span className="font-semibold">Avg: </span>
                                  <span className="text-teal-700 font-bold">{assessment.summary.averageScore}</span>
                                  <span className="text-gray-500">/7</span>
                                </div>
                                {assessment.summary.editedCount > 0 && (
                                  <div className="text-blue-600">
                                    <span className="font-semibold">✏️ </span>
                                    {assessment.summary.editedCount} edited
                                  </div>
                                )}
                                {assessment.summary.flaggedForReview > 0 && (
                                  <div className="text-amber-600">
                                    <span className="font-semibold">⚠ </span>
                                    {assessment.summary.flaggedForReview} flagged
                                  </div>
                                )}
                              </div>
                            )}
                      </div>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewAssessment(assessment);
                            }}
                            className="text-sm text-teal-600 hover:text-teal-700 font-semibold hover:underline"
                          >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <FileText className="mx-auto text-gray-400 mb-3" size={48} />
                <p className="text-gray-600">No assessment history yet</p>
                <p className="text-sm text-gray-500 mt-1">Create your first assessment for this case</p>
              </div>
            )}
          </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => {
              onNewAssessment(caseItem);
              onClose();
            }}
            className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
          >
            <Plus size={20} />
            New Assessment
          </button>
        </div>
      </div>

      {/* Assessment Details Modal */}
      <AssessmentDetailsModal
        isOpen={showAssessmentModal}
        onClose={handleCloseAssessmentModal}
        assessment={selectedAssessment}
        caseName={caseItem?.name}
      />
    </div>
  );
};

export default CaseDetailsModal;

